import type { OpeningSide } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { planeReProjectPolyLine2 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { makeOpeningSide } from "./openings/openingProfile";

export function wallCutNew({
  edgeParam,
  edgeParamNormalized,
  edgeParamAbsolute,
  width,
  height,
  bottom,
  opening,
}: {
  readonly edgeParam: number;
  readonly edgeParamNormalized: boolean;
  readonly edgeParamAbsolute?: boolean;
  readonly width: number;
  readonly height: number;
  readonly bottom: number;
  readonly opening: WallOpening | undefined;
}): WallCut {
  return {
    type: "WALL_CUT",
    edgeParam,
    edgeParamNormalized,
    edgeParamAbsolute,
    width,
    height,
    bottom,
    opening,
  };
}

export function makeWallCutOutline(
  { width, height, bottom, edgeParam, edgeParamNormalized, edgeParamAbsolute = false, opening }: WallCut,
  edgeLength: number,
  destinationPlane: Plane,
): OpeningSide | null {
  let centerParam = edgeParam;

  if (edgeParamNormalized) {
    centerParam = edgeLength * edgeParam;
  }

  if (edgeParam < 0 && !edgeParamAbsolute) {
    centerParam = edgeLength + centerParam;
  }

  const top = height + bottom;

  const halfWidth = width / 2;
  const startParam = centerParam - halfWidth;
  const endParam = centerParam + halfWidth;

  if (!edgeParamAbsolute && (endParam > edgeLength || endParam < 0 || startParam > edgeLength || startParam < 0)) {
    return null;
  }

  const outline = opening?.back.openingCasing2;

  let openingLoop: PolyLine2;
  if (outline && outline.length > 0) {
    openingLoop = planeReProjectPolyLine2(opening.back.openingCasing2, opening.backPlane, destinationPlane);
  } else {
    openingLoop = polyLine2New([
      startParam,
      bottom,
      endParam,
      bottom,
      endParam,
      top,
      startParam,
      top,
      startParam,
      bottom,
    ]);
  }

  return makeOpeningSide({
    openingRect2: openingLoop,
    opening2: openingLoop,
    startParam,
    endParam,
    context: {
      openingPlane: destinationPlane,
    },
  });
}
